import { RxCross2 } from '@react-icons/all-files/rx/RxCross2'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  PublishingSplitData,
  hasSplitFieldValue,
  type SplitData,
} from '@disco/state-management/reducers/rightsManagement'

import type {
  RightsHolderEntity,
  SplitValidationError,
} from 'types/rights-data'

import { Box, Flex, IconButton, Table, Text, TextField } from 'components/ds'
import type { SearchableDropdownOption } from 'components/ds/SearchableDropdown'
import { SearchableDropdown } from 'components/ds/SearchableDropdown'
import { CreateNewAction } from 'components/ds/SearchableDropdown/CreateNewAction'
import { OptionWithEditIcon } from 'components/ds/SearchableDropdown/OptionWithEditIcon'

import { RIGHTS_DROPDOWN_WIDTH } from '../../../constants'
import { formatIdentifiersForDisplay } from '../../../utils/identifier-validation'

import styles from '../styles.module.css'
import { filterRightsHolderOptions, findErrorsForField } from './utils'

interface PublishingRowProps {
  split: PublishingSplitData
  /** Splits key (track ID or BULK_EDIT_KEY) for updating the correct split after entity creation */
  splitsKey: number | string
  /** Field-level validation errors for this split */
  validationError?: SplitValidationError
  /** All writers from the single API call (used for exclusion list) */
  writers: RightsHolderEntity[]
  /** All publishers from the single API call */
  publishers: RightsHolderEntity[]
  /** All PROs from the single API call */
  pros: RightsHolderEntity[]
  /** Whether rights holders data is still loading */
  isRightsHoldersLoading: boolean
  onUpdate: (
    splitId: number | string,
    changes: Partial<Omit<PublishingSplitData, 'id'>>,
  ) => void
  onDelete: (splitId: number | string) => void
  /** Callback to set/clear a field-level validation error */
  onValidate?: (
    splitId: number | string,
    field: string,
    message: string,
  ) => void
  /** Callback to clear all validation errors for a split */
  onClearValidation?: (splitId: number | string) => void
  /** Disables all form inputs (used while a save is in flight) */
  disabled?: boolean
}

/**
 * PublishingRow - A table row for editing a single publishing rights split
 *
 * Includes Writer, Publisher, PRO, and Split columns with edit/create functionality.
 * Uses pre-fetched rights holders data from the single /api/rights_data/rights_holders/ API call.
 */
export function PublishingRow({
  split,
  splitsKey,
  validationError,
  writers,
  publishers,
  pros,
  isRightsHoldersLoading,
  onUpdate,
  onDelete,
  onValidate,
  onClearValidation,
  disabled = false,
}: PublishingRowProps) {
  const navigate = useNavigate()

  const [splitInput, setSplitInput] = useState<string>(
    split.split == null ? '' : String(split.split),
  )

  const [writerSearch, setWriterSearch] = useState('')
  const [publisherSearch, setPublisherSearch] = useState('')
  const [proSearch, setProSearch] = useState('')

  const hasWriter = hasSplitFieldValue(split.writerId)
  const hasPublisher = hasSplitFieldValue(split.publisherId)
  const hasPro = hasSplitFieldValue(split.proId)
  const hasSplit = split.split != null
  const hasAnyFieldFilled = hasWriter || hasPublisher || hasPro || hasSplit
  const isEmptyPlaceholder = !hasAnyFieldFilled

  // Cross-field validation: when any field is filled, validate required fields
  useEffect(() => {
    if (!onValidate || !onClearValidation) return

    if (isEmptyPlaceholder) {
      onClearValidation(split.id)
    } else {
      if (!hasWriter) {
        onValidate(split.id, 'writer', 'Please add a writer')
      } else {
        onValidate(split.id, 'writer', '')
      }

      // Only validate split > 100% in real-time
      if ((split.split ?? 0) > 100) {
        onValidate(split.id, 'split_percentage', 'Cannot exceed 100%')
      } else {
        onValidate(split.id, 'split_percentage', '')
      }
    }
  }, [
    split.id,
    hasWriter,
    hasPublisher,
    hasPro,
    split.split,
    isEmptyPlaceholder,
    onValidate,
    onClearValidation,
  ])

  const errorFieldTypes = ['writer', 'publisher', 'pro', 'split_percentage']
  const [writerError, publisherError, proError, splitError] =
    findErrorsForField(validationError, errorFieldTypes)

  const writerOptions = useMemo(
    () => filterRightsHolderOptions(writers, writerSearch),
    [writers, writerSearch],
  )

  const publisherOptions = useMemo(
    () => filterRightsHolderOptions(publishers, publisherSearch),
    [publishers, publisherSearch],
  )

  const proOptions = useMemo(
    () => filterRightsHolderOptions(pros, proSearch),
    [pros, proSearch],
  )

  const renderWriterOption = useCallback(
    (option: SearchableDropdownOption<RightsHolderEntity>) => {
      const secondaryText = formatIdentifiersForDisplay(
        option.data?.external_identifiers,
      )
      return (
        <OptionWithEditIcon
          label={option.label}
          secondaryText={secondaryText}
          onEditClick={() => {
            navigate(`rights-data/writers/${option.id}/edit`, {
              state: { splitId: split.id, splitsKey },
            })
          }}
        />
      )
    },
    [navigate, split.id, splitsKey],
  )

  const renderPublisherOption = useCallback(
    (option: SearchableDropdownOption<RightsHolderEntity>) => {
      const secondaryText = formatIdentifiersForDisplay(
        option.data?.external_identifiers,
      )
      return (
        <OptionWithEditIcon
          label={option.label}
          secondaryText={secondaryText}
          onEditClick={() => {
            navigate(`rights-data/publishers/${option.id}/edit`, {
              state: { splitId: split.id, splitsKey },
            })
          }}
        />
      )
    },
    [navigate, split.id, splitsKey],
  )

  const renderProOption = useCallback(
    (option: SearchableDropdownOption<RightsHolderEntity>) => {
      const secondaryText = formatIdentifiersForDisplay(
        option.data?.external_identifiers,
      )
      return (
        <OptionWithEditIcon
          label={option.label}
          secondaryText={secondaryText}
          onEditClick={() => {
            navigate(`rights-data/pros/${option.id}/edit`, {
              state: { splitId: split.id, splitsKey },
            })
          }}
        />
      )
    },
    [navigate, split.id, splitsKey],
  )

  const writerActionComponent = useCallback(
    (searchValue: string) => (
      <CreateNewAction
        label="Create new writer"
        to="rights-data/writers/new"
        searchText={searchValue}
        splitId={split.id}
        splitsKey={splitsKey}
      />
    ),
    [split.id, splitsKey],
  )

  const publisherActionComponent = useCallback(
    (searchValue: string) => (
      <CreateNewAction
        label="Create new publisher"
        to="rights-data/publishers/new"
        searchText={searchValue}
        splitId={split.id}
        splitsKey={splitsKey}
      />
    ),
    [split.id, splitsKey],
  )

  const proActionComponent = useCallback(
    (searchValue: string) => (
      <CreateNewAction
        label="Create new PRO"
        to="rights-data/pros/new"
        searchText={searchValue}
        splitId={split.id}
        splitsKey={splitsKey}
      />
    ),
    [split.id, splitsKey],
  )

  const handleWriterChange = useCallback(
    (option: SearchableDropdownOption<RightsHolderEntity> | null) => {
      if (!option) {
        onUpdate(split.id, {
          writerId: null,
        })
        return
      }

      const newWriterId = parseInt(option.id, 10)

      if (newWriterId === split.writerId) {
        return
      }

      onUpdate(split.id, {
        writerId: newWriterId,
        // Note: auto-populate from writer defaults is not available in new API yet
        // Users will need to select publisher/PRO manually until SLAM-1600 is implemented
      })

      // Clear writer validation error when valid writer is selected
      if (onValidate && newWriterId > 0) {
        onValidate(split.id, 'writer', '')
      }
    },
    [split.id, split.writerId, onUpdate, onValidate],
  )

  const handlePublisherChange = useCallback(
    (option: SearchableDropdownOption<RightsHolderEntity> | null) => {
      onUpdate(split.id, {
        publisherId: option ? parseInt(option.id, 10) : null,
      })
    },
    [split.id, onUpdate],
  )

  const handleProChange = useCallback(
    (option: SearchableDropdownOption<RightsHolderEntity> | null) => {
      onUpdate(split.id, {
        proId: option ? parseInt(option.id, 10) : null,
      })
    },
    [split.id, onUpdate],
  )

  const handleSplitChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      // Allow empty string, digits, and up to 2 decimal places
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        setSplitInput(value)
        const parsed = parseFloat(value)
        const nextSplit = value === '' ? null : isNaN(parsed) ? null : parsed
        onUpdate(split.id, { split: nextSplit })
      }
    },
    [split.id, onUpdate],
  )

  // Handler for split percentage blur to format display value
  const handleSplitBlur = useCallback(() => {
    if (splitInput === '') return
    const numValue = parseFloat(splitInput)
    if (isNaN(numValue)) {
      setSplitInput('')
      return
    }
    const roundedValue = Math.round(numValue * 100) / 100
    setSplitInput(String(roundedValue))
  }, [splitInput])

  const handleDelete = useCallback(() => {
    onDelete(split.id)
  }, [split.id, onDelete])

  const writerValue =
    typeof split.writerId === 'number' ? String(split.writerId) : undefined
  const publisherValue =
    typeof split.publisherId === 'number'
      ? String(split.publisherId)
      : undefined
  const proValue =
    typeof split.proId === 'number' ? String(split.proId) : undefined

  return (
    <Table.Row>
      <Table.Cell>
        <Box className={writerError ? styles.fieldWithError : undefined}>
          <SearchableDropdown
            value={writerValue}
            onSelect={handleWriterChange}
            onSearchChange={setWriterSearch}
            options={writerOptions}
            loading={isRightsHoldersLoading}
            placeholder="Writer"
            renderOption={renderWriterOption}
            actionComponent={writerActionComponent}
            aria-label="Writer dropdown"
            maxHeight={208}
            customDropdownWidth={RIGHTS_DROPDOWN_WIDTH}
            disabled={disabled}
          />
          {writerError && (
            <Text size="1" className={styles.errorMessage}>
              {writerError}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Box className={publisherError ? styles.fieldWithError : undefined}>
          <SearchableDropdown
            value={publisherValue}
            onSelect={handlePublisherChange}
            onSearchChange={setPublisherSearch}
            options={publisherOptions}
            loading={isRightsHoldersLoading}
            placeholder="Publisher"
            renderOption={renderPublisherOption}
            actionComponent={publisherActionComponent}
            aria-label="Publisher dropdown"
            maxHeight={208}
            customDropdownWidth={RIGHTS_DROPDOWN_WIDTH}
            disabled={disabled}
          />
          {publisherError && (
            <Text size="1" className={styles.errorMessage}>
              {publisherError}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Box className={proError ? styles.fieldWithError : undefined}>
          <SearchableDropdown
            value={proValue}
            onSelect={handleProChange}
            onSearchChange={setProSearch}
            options={proOptions}
            loading={isRightsHoldersLoading}
            placeholder="PRO"
            renderOption={renderProOption}
            actionComponent={proActionComponent}
            aria-label="PRO dropdown"
            maxHeight={208}
            customDropdownWidth={RIGHTS_DROPDOWN_WIDTH}
            disabled={disabled}
          />
          {proError && (
            <Text size="1" className={styles.errorMessage}>
              {proError}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Box className={splitError ? styles.fieldWithError : undefined}>
          <TextField.Root
            value={splitInput}
            onChange={handleSplitChange}
            onBlur={handleSplitBlur}
            placeholder="Split"
            color={splitError ? 'red' : undefined}
            disabled={disabled}
          />
          {splitError && (
            <Text size="1" className={styles.errorMessage}>
              {splitError}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell justify="center" width="24px" maxWidth="24px">
        <Flex justify="center" align="start" height="100%" mt="2">
          <IconButton
            variant="ghost"
            color="gray"
            size="1"
            onClick={handleDelete}
            aria-label="Remove writer"
            disabled={disabled}
          >
            <RxCross2 />
          </IconButton>
        </Flex>
      </Table.Cell>
    </Table.Row>
  )
}

export default PublishingRow
